import { Dispatch, SetStateAction, useRef, ChangeEvent, useMemo } from "react"
import * as Yup from 'yup';
import { promiseWithResolvers } from "@/utils/promiseWithResolvers";


interface ExtraProps<S extends { prevData: any, stage: any, errors: any, touched: any }> {
  setValueProp?: (path: keyof S['stage'], hookSetVal: (newValue: any) => void) => (newValue: any) => void
  validationContext?: any,
}


export function usePlainStateFormUtils<S extends { prevData: any, stage: any, errors: any, touched: any }>(
  states: S, 
  setStates: Dispatch<SetStateAction<S>>, validateSchema: Yup.AnySchema, 
  extraProps?: ExtraProps<S>,
) {
  const { setValueProp, validationContext } = extraProps ?? {}
  const vctxRef = useRef(validationContext)
  vctxRef.current = validationContext

  const {validate, defaultSetValue, trySubmitValidate, commonMutate} = useMemo(() => {
    const validate = (x: S['stage']) => {
      try {
        validateSchema.validateSync(x, {abortEarly:false, context: vctxRef.current})
      } catch (e: any) {
        if (e.name === 'ValidationError') {
          const errors = {} as Record<string, string>
          for (const err of e.inner) {
            errors[err.path] = err.message
          }
          return errors
        }
      }
    }
    const commonMutate = (path: keyof S['stage'], newValue: any) => (v: S) => {
      (v.stage as any)[path] = newValue
      if (v.prevData[path] !== newValue) {
        v.touched[path] = true
      } else {
        delete v.touched[path]
      }
      v.errors = validate(v.stage) ?? {}
      return {...v}
    }
    const defaultSetValue = (path: keyof S['stage']) => (newValue: any) => {
      setStates(commonMutate(path, newValue))
    }
    const trySubmitValidate = () => {
      const { promise, resolve, reject } = promiseWithResolvers<S>()
      setStates(v => {
        const v1 = {...v}
        const errors = validate(v1.stage)
        v1.errors = errors ?? {};
        // v1.touched = Object.keys(v1.stage).reduce((acc, k) => ({...acc, [k]: true}), {})
        toolTouch(v1).setTouch()

        
        if (!errors || Object.keys(errors).length === 0) {
          resolve(v1)
        } else {
          reject(errors)
        }
        return v1
      })
      return promise
    }
    return ({validate, defaultSetValue, trySubmitValidate, commonMutate})
  }, [setStates, validateSchema])

  const timerRef = useRef({} as any)
  const fieldProps = (path: keyof S['stage'], transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (timerRef.current[path]) {
        window.clearTimeout(timerRef.current[path])
        delete timerRef.current[path]
      }
      const value = transform ? transform(e.target.value) : e.target.value;
      const defaultSetValueFn = defaultSetValue(path)
      const setValue = setValueProp ? setValueProp(path, defaultSetValueFn) : defaultSetValueFn
      setValue(value)
      if (typeof value === 'string' && value.trim() !== value) {
        timerRef.current[path] = setTimeout(() => setValue(value.trim()), 1500)
      }
    },
    error: Boolean((states.touched[path] || toolTouch(states).testPath(path)) && states.errors[path]),
    helperText: (states.touched[path] || toolTouch(states).testPath(path)) ? states.errors[path] : undefined,
  })
  return {validate, fieldProps, trySubmitValidate, commonMutate}
}

// when trySubmitValidte(), we want every field been "touched" so that validation error will be displayed
// but the "touched" state is better to be seperated with user input touches
const toolTouch = (states: any) => ({
  hasToolTouch: () => states['__'],
  deleteToolTouch: (path: string | number | symbol) => { 
    if (states['__']) {
      delete states['__'][path]
    }
  },
  setTouch: () => {
    states['__'] = Object.keys(states.stage).reduce((acc, k) => ({...acc, [k]: true}), {})
  },
  testPath: (path: string | number | symbol) => states['__'] && states['__'][path],
})

export interface UIFormState<S = any, P = S> {
  prevData: P,
  stage: S,
  errors: any,
  touched: any
}

export type StateTouched<S> = {
  [K in keyof S]?: boolean;
}
export type StateErrors<S> = {
  [K in keyof S]?: string; } & {
  [k:string]: string;
}